import React, { useEffect, useRef, useState } from "react";

import { appf_tenant_info } from "../UserPool";

/**
 * The bridge's own app, where the tenant has one.
 * ---------------------------------------------------------------------------
 * `tenant_info.json` says whether this installation has an MCP bridge and
 * where it lives: `"mcp-bridge": "https://…"`. That address is a web app of
 * its own, so this panel is a window onto it rather than a second set of
 * controls for the same thing.
 *
 * Whether it will BE a window is a question the browser only half answers,
 * and this was measured rather than assumed:
 *
 *   a bridge on THIS origin     refused → contentDocument null
 *                               shown   → contentDocument readable
 *   a bridge on another origin  refused → contentDocument null
 *                               shown   → contentDocument null
 *
 * The load event is no help either: it fired at 3ms for a bridge that refuses
 * and 4ms for one that does not, because both are the frame's initial blank
 * document.
 *
 * Same-origin, then, a refusal is plain to see: the frame goes up invisible,
 * is asked, and is either revealed or taken away before anything paints.
 * Cross-origin there is nothing to see - CORS keeps the other site's
 * `X-Frame-Options` and `frame-ancestors` from us, and a blocked frame is
 * indistinguishable from a working one - so it is not framed on spec: the
 * address, a button that always works, and one press to try it here for an
 * installation whose bridge does allow it. Either answer is remembered per
 * address.
 * ---------------------------------------------------------------------------
 */

/* What the test found, per bridge: "yes" or "no". */
const VERDICT_KEY = (url) => `ui.mcp.frames.${url}`;

/* The frame is asked every so often until it can answer, and given this long
   in total before silence is taken for a refusal. Asked repeatedly rather than
   once after a delay: a single timer is lost if anything re-mounts this panel
   while it is running, and then the question is never answered at all. */
const ASK_EVERY = 250;
const GIVE_UP = 3000;

/* Read through the key the tenant file uses, and the two spellings a later
   version of that file might reasonably use instead. */
export function mcpBridgeUrl() {
  const t = appf_tenant_info || {};
  const raw = t["mcp-bridge"] || t.mcp_bridge || t.mcpBridge || "";
  const url = String(raw).trim();
  if (!url) return "";
  /* only ever a web address: a tenant file is configuration, but it is still
     something this app is handed, and `javascript:` is not an address */
  return /^https?:\/\//i.test(url) ? url : "";
}

/**
 * Did the frame load the bridge, or is it still on the blank page the browser
 * left it on when it refused?
 *
 * Reading `contentDocument` is the test: same-origin (so readable) means the
 * navigation never happened. Anything thrown - or a null document - means
 * another origin is in there, which is what success looks like from out here.
 */
function framedOk(frame) {
  try {
    const doc = frame.contentDocument;
    if (!doc) return true;                       /* cross-origin: it loaded */
    const href = doc.location && doc.location.href;
    if (href && href !== "about:blank") return true;
    return false;                                /* still blank: refused */
  } catch (e) {
    return true;                                 /* cross-origin: it loaded */
  }
}

/* Is the bridge on this very origin? Then the test can see everything. */
function sameOrigin(url) {
  try { return new URL(url, window.location.href).origin === window.location.origin; }
  catch (e) { return false; }
}

export default function McpBridgeSettings({ isDarkMode }) {
  const url = mcpBridgeUrl();
  const here = sameOrigin(url);

  /* "asking" | "yes" | "no" | "unknown"
     Same-origin it is asked and answered. Cross-origin it cannot be asked, so
     it starts at "unknown": not framed, and one press away from being. */
  /* Same-origin the test runs and decides. Cross-origin there is no test to
     run - the browser tells an embedding page nothing about another site's
     framing rules - so the frame simply goes up: a bridge that allows framing
     shows itself, which is the whole point, and one that refuses shows the
     browser's own blank page with the address and a way out above it. */
  const [verdict, setVerdict] = useState(() => {
    if (!here) return "yes";
    let saved = null;
    try { saved = localStorage.getItem(VERDICT_KEY(url)); } catch (e) { /* private window */ }
    return saved || "asking";
  });
  const frameRef = useRef(null);

  useEffect(() => {
    if (!url || verdict !== "asking") return undefined;
    const started = Date.now();
    const settle = (said) => {
      /* only the test's own answer is remembered, and only where it can be run */
      if (here) {
        try { localStorage.setItem(VERDICT_KEY(url), said); } catch (e) { /* private window */ }
      }
      setVerdict(said);
    };
    const ask = () => {
      const frame = frameRef.current;
      /* loaded: answered. still blank: ask again, until there is no point. */
      if (frame && framedOk(frame)) { clearInterval(every); settle("yes"); return; }
      if (Date.now() - started >= GIVE_UP) { clearInterval(every); settle("no"); }
    };
    const every = setInterval(ask, ASK_EVERY);
    return () => clearInterval(every);
  }, [url, verdict, here]);

  const ink = isDarkMode ? "rgba(245,239,230,0.9)" : "rgba(17,17,17,0.9)";
  const dim = isDarkMode ? "rgba(245,239,230,0.5)" : "rgba(17,17,17,0.55)";
  const hair = isDarkMode ? "rgba(245,239,230,0.12)" : "rgba(17,17,17,0.12)";

  if (!url) {
    return (
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: dim }}>
        No bridge is configured for this installation.
      </p>
    );
  }

  /* Framed only where it is known to work: the test said yes, or someone did.
     "unknown" and "no" both mean no frame - the difference is what is said
     about it. */
  const framed = verdict === "yes" || verdict === "asking";
  const refused = verdict === "no";

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 14,
      height: "100%",
      minHeight: 0,
      color: ink,
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "11px 13px", border: `1px solid ${hair}`, borderRadius: 10,
        flex: "0 0 auto",
      }}>
        <span style={{
          flex: "1 1 auto", minWidth: 0,
          fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 11.5, color: dim,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {url}
        </span>

        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          style={{
            flex: "0 0 auto", height: 30, display: "inline-flex", alignItems: "center",
            padding: "0 14px", borderRadius: 8,
            border: refused ? "1px solid rgb(246,64,13)" : `1px solid ${hair}`,
            background: refused ? "rgb(246,64,13)" : "transparent",
            color: refused ? "#fff" : ink, fontSize: 12.5, textDecoration: "none",
          }}
        >
          Open in a tab
        </a>
      </div>

      {/* While being asked, and ever after if the answer was yes. Invisible
          for the moment the question takes, so a refusal never paints. */}
      {framed && (
        <div style={{
          position: "relative",
          flex: "1 1 auto",
          minHeight: 360,
          border: `1px solid ${hair}`,
          borderRadius: 10,
          overflow: "hidden",
          background: isDarkMode ? "#0b0b0b" : "#f7f5f2",
        }}>
          <iframe
            ref={frameRef}
            src={url}
            title="MCP bridge"
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-downloads"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              width: "100%", height: "100%", border: 0, display: "block",
              opacity: verdict === "yes" ? 1 : 0,
              transition: "opacity 160ms cubic-bezier(0.2, 0, 0, 1)",
            }}
          />

          {verdict === "asking" && (
            <span style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12.5, color: dim, pointerEvents: "none",
            }}>
              Opening the bridge…
            </span>
          )}
        </div>
      )}
    </div>
  );
}
